<template>
  <section
    class="mx-auto w-full bg-white px-5 pb-16 pt-[52px]"
    aria-labelledby="titulo-consejos-frenos"
  >
    <div class="mx-auto max-w-[1200px]">
      <div class="mb-11 text-center">
        <div
          class="mb-2.5 inline-flex items-center gap-2 text-[#C8102E]"
          aria-hidden="true"
        >
          <span class="h-[2px] w-7 rounded-full bg-[#C8102E]" />
          <span class="text-[13px] font-semibold uppercase leading-none tracking-[0.14em]">
            {{ eyebrow }}
          </span>
          <span class="h-[2px] w-7 rounded-full bg-[#C8102E]" />
        </div>

        <h2
          id="titulo-consejos-frenos"
          class="m-0 text-[28.8px] font-semibold leading-[1.25] text-[#33393F]"
        >
          {{ title }}
        </h2>
      </div>

      <div class="grid grid-cols-1 gap-[22px] md:grid-cols-2 lg:grid-cols-3">
        <article
          v-for="item in items"
          :key="item.id"
          class="group flex min-h-[267px] flex-col rounded-[12px] border border-[#E8E8E8] bg-[#FCFCFC] px-[26px] pb-[26px] pt-7 transition duration-200 ease-out hover:-translate-y-[3px] hover:shadow-[0_10px_28px_rgba(0,0,0,0.08)]"
          @mouseenter="hoveredCard = item.id"
          @mouseleave="hoveredCard = null"
        >
          <header class="mb-3.5 flex items-start justify-between gap-4">
            <h3 class="m-0 pt-1 text-[18.9px] font-semibold leading-[1.3] text-[#33393F]">
              {{ item.title }}
            </h3>

            <div class="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[12px] bg-[#FDF1F3]">
              <img
                :src="hoveredCard === item.id ? item.icon : item.iconStatic"
                :alt="item.alt ?? item.title"
                width="38"
                height="38"
                class="object-contain"
              >
            </div>
          </header>

          <div class="flex flex-1 flex-col">
            <p class="m-0 flex-1 text-[15.52px] leading-[1.62] text-[#55595E]">
              {{ item.description }}
            </p>

            <button
              v-if="item.modal"
              type="button"
              :aria-label="`Ver más sobre ${item.title}`"
              class="mt-5 inline-flex w-fit items-center gap-[9px] rounded-[8px] border-[1.5px] border-[#C8102E] bg-white px-5 py-2.5 text-[15.2px] font-medium leading-none text-[#C8102E] transition-colors duration-200 hover:bg-[#C8102E] hover:text-white focus:outline-none focus:ring-2 focus:ring-[#C8102E] focus:ring-offset-2"
              @click="openModal(item.id)"
            >
              Ver más
              <svg
                viewBox="0 0 24 24"
                class="h-[15px] w-[15px] fill-none stroke-current stroke-[2.4]"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M7 17 17 7M9 7h8v8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>
        </article>
      </div>
    </div>

    <!-- Modal -->
    <Transition name="modal-overlay">
      <div
        v-if="activeItem"
        class="fixed inset-0 z-[9999] flex items-center justify-center bg-[rgba(20,20,20,0.55)] p-5"
        role="dialog"
        :aria-labelledby="`consejos-modal-title-${activeItem.id}`"
        aria-modal="true"
        @click.self="closeModal"
      >
        <div class="modal-inner flex max-h-[88vh] w-full max-w-[620px] flex-col overflow-hidden rounded-[14px] bg-white">
          <!-- Cabecera -->
          <div class="relative flex items-center gap-4 border-b border-[#E8E8E8] px-[26px] pb-[18px] pt-6">
            <div class="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[12px] bg-[#FDF1F3]">
              <img
                :src="activeItem.icon"
                :alt="activeItem.alt ?? activeItem.title"
                width="32"
                height="32"
                class="object-contain"
              >
            </div>

            <h3
              :id="`consejos-modal-title-${activeItem.id}`"
              class="m-0 pr-10 text-[1.15rem] font-semibold leading-[1.3] text-[#33393F]"
            >
              {{ activeItem.title }}
            </h3>

            <button
              ref="closeButtonRef"
              type="button"
              class="absolute right-4 top-3 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C8102E]"
              aria-label="Cerrar"
              @click="closeModal"
            >
              <img
                src="/img/frenos/icon/Close.svg"
                width="40"
                height="40"
                aria-hidden="true"
              >
            </button>
          </div>

          <!-- Cuerpo -->
          <div class="overflow-y-auto px-[26px] py-6">
            <p
              v-for="(para, i) in activeItem.modal?.paragraphs ?? []"
              :key="i"
              class="mb-[14px] text-[0.97rem] leading-[1.7] text-[#55595E] last:mb-0 [&_strong]:text-[#C8102E]"
              v-html="para"
            />

            <div
              v-if="activeItem.modal?.tip"
              class="mt-[6px] rounded-[10px] border border-[#CBEBD6] bg-[#F2FBF5] px-4 py-[14px] text-[0.97rem] leading-[1.7] text-[#55595E] [&_strong]:text-[#16A34A]"
              v-html="activeItem.modal.tip"
            />
          </div>
        </div>
      </div>
    </Transition>
  </section>
</template>

<script setup lang="ts">
export interface ConsejoModal {
  paragraphs: string[]
  tip?: string
}

export interface ConsejoFreno {
  id: number | string
  title: string
  description: string
  icon: string
  iconStatic: string
  alt?: string
  modal?: ConsejoModal
}

interface Props {
  eyebrow?: string
  title?: string
  items?: ConsejoFreno[]
}

const props = withDefaults(
  defineProps<Props>(),
  {
    eyebrow: 'CONSEJOS DE FRENOS',
    title: 'Cómo cuidar y mantener tu Sistema de Frenado',
    items: () => [
      {
        id: 1,
        title: 'Revisa tus frenos cada 10.000 km',
        description: 'Una revisión preventiva a tiempo detecta el desgaste de pastillas y discos antes de que se transforme en una reparación mayor.',
        icon: '/img/frenos/icon-animated/frenos.gif',
        iconStatic: '/img/frenos/icon-animated/frenos.png',
        alt: 'Revisa tus frenos',
        modal: {
          paragraphs: [
            'Las pastillas de freno se desgastan de forma gradual y silenciosa: cuando el ruido aparece, muchas veces el daño ya alcanzó los discos, lo que encarece la reparación.',
            'Te recomendamos revisar el sistema de frenos <strong>cada 10.000 km o al menos una vez al año</strong>, y siempre antes de un viaje largo en carretera. En la revisión se controla el espesor de pastillas y discos, el estado del cáliper, las flexibles y el nivel y condición del líquido de frenos.'
          ],
          tip: '<strong>Dato LEÓN:</strong> en todos nuestros locales el diagnóstico de frenos es gratuito y sin compromiso.'
        }
      },
      {
        id: 2,
        title: 'Atento a las señales de alerta',
        description: 'Chirridos, vibraciones en el pedal o un frenado menos firme son avisos de que algo no anda bien en tu sistema de frenos.',
        icon: '/img/frenos/icon-animated/alerta.gif',
        iconStatic: '/img/frenos/icon-animated/alerta.png',
        alt: 'Atento a alertas',
        modal: {
          paragraphs: [
            'Tu auto te avisa cuando los frenos necesitan atención. Estas son las señales más comunes:',
            '<strong>Chirrido o ruido metálico al frenar:</strong> pastillas desgastadas o sucias haciendo fricción sobre los discos.',
            '<strong>Vibración en el pedal o el volante:</strong> discos deformados o rodamientos gastados.',
            '<strong>Pedal esponjoso o que baja demasiado:</strong> posible aire o humedad en el circuito hidráulico.',
            '<strong>El auto se carga hacia un lado al frenar:</strong> desgaste desparejo o cáliper trabado.'
          ],
          tip: '<strong>Importante:</strong> si detectas cualquiera de estas señales, no lo dejes pasar. Frenar bien no es opcional.'
        }
      },
      {
        id: 3,
        title: 'Cambia el líquido de frenos cada 2 años',
        description: 'El líquido de frenos absorbe humedad con el tiempo y pierde eficacia, aunque el nivel se vea correcto en el depósito.',
        icon: '/img/frenos/icon-animated/liquido-frenos.gif',
        iconStatic: '/img/frenos/icon-animated/liquido-frenos.png',
        alt: 'Cambia líquido de frenos',
        modal: {
          paragraphs: [
            'El líquido de frenos es higroscópico: <strong>absorbe humedad del ambiente</strong> con el paso del tiempo. Esa humedad baja su punto de ebullición y, en frenadas exigentes, puede generar burbujas de vapor que hacen que el pedal pierda firmeza.',
            'Aunque el nivel del depósito se vea correcto, la condición del líquido se degrada igual. Por eso se recomienda reemplazarlo <strong>cada 2 años o 40.000 km</strong>, lo que ocurra primero, junto con una purga completa del circuito.'
          ],
          tip: '<strong>Dato LEÓN:</strong> en tu próxima mantención pide que midan la humedad del líquido de frenos; es un chequeo rápido que puede evitarte un susto.'
        }
      },
      {
        id: 4,
        title: 'Frena con anticipación',
        description: 'Los frenazos bruscos desgastan pastillas y discos hasta 3 veces más rápido. Anticiparte al tráfico alarga la vida de tus frenos.',
        icon: '/img/frenos/icon-animated/anticipacion.gif',
        iconStatic: '/img/frenos/icon-animated/anticipacion.png',
        alt: 'Frena anticipado',
        modal: {
          paragraphs: [
            'La forma en que conduces influye directamente en la duración de tus frenos. Los frenazos bruscos y frenar a última hora generan altas temperaturas que <strong>desgastan pastillas y discos hasta 3 veces más rápido</strong>.',
            'Mantén una distancia prudente con el vehículo de adelante, suelta el acelerador con anticipación y deja que el auto pierda velocidad de forma natural antes de pisar el pedal. Además de cuidar tus frenos, reducirás el consumo de combustible.'
          ],
          tip: '<strong>Dato LEÓN:</strong> evita circular con el pie apoyado en el pedal de freno; ese roce constante también acelera el desgaste.'
        }
      },
      {
        id: 5,
        title: 'Usa freno motor en bajadas largas',
        description: 'En cuestas prolongadas, apoyarte solo en el pedal recalienta el sistema y puede provocar pérdida de frenado.',
        icon: '/img/frenos/icon-animated/bajadas.gif',
        iconStatic: '/img/frenos/icon-animated/bajadas.png',
        alt: 'Freno motor',
        modal: {
          paragraphs: [
            'En bajadas prolongadas —como las cuestas de la costa o rutas de montaña— usar solo el pedal de freno somete el sistema a un calor extremo. Esto puede producir <strong>fatiga o "fading": pérdida de capacidad de frenado en caliente</strong>, justo cuando más lo necesitas.',
            'La técnica correcta es bajar en una <strong>marcha más corta</strong> (en automáticos, usa el modo manual o posiciones L/2) para que el motor retenga el vehículo, y usar el freno solo de apoyo, en pisadas firmes y breves.'
          ],
          tip: '<strong>Importante:</strong> si sientes olor a quemado o el pedal pierde firmeza en una bajada, detente en un lugar seguro y deja enfriar los frenos.'
        }
      },
      {
        id: 6,
        title: 'Cambia pastillas y discos por eje',
        description: 'Reemplazar los componentes siempre en pares y con repuestos de calidad asegura un frenado parejo y seguro.',
        icon: '/img/frenos/icon-animated/ejes.gif',
        iconStatic: '/img/frenos/icon-animated/ejes.png',
        alt: 'Cambio por eje',
        modal: {
          paragraphs: [
            'Las pastillas y los discos siempre deben reemplazarse <strong>en pares, por eje completo</strong> (las dos ruedas delanteras o las dos traseras). Cambiar solo un lado genera un frenado desparejo que puede desviar el auto en una frenada de emergencia.',
            'La duración de las pastillas varía según la marca, el modelo y tu estilo de conducción: en ciudad suelen durar entre <strong>30.000 y 40.000 km</strong>, y en carretera pueden superar los 60.000 km. Los discos, en general, duran el equivalente a dos juegos de pastillas.',
            'Prefiere siempre repuestos de calidad certificada: en LEÓN trabajamos con marcas líderes como ACDelco, Textar, Penflex y Sangsin.'
          ],
          tip: '<strong>Dato LEÓN:</strong> todos nuestros servicios incluyen garantía de 6 meses o 10.000 km.'
        }
      }
    ]
  }
)

const hoveredCard = ref<number | string | null>(null)
const activeModal = ref<number | string | null>(null)
const lastFocused = ref<Element | null>(null)
const closeButtonRef = ref<HTMLButtonElement | null>(null)

const activeItem = computed(() =>
  activeModal.value !== null
    ? props.items?.find(i => i.id === activeModal.value) ?? null
    : null
)

function openModal(id: number | string) {
  lastFocused.value = document.activeElement
  activeModal.value = id
  document.body.style.overflow = 'hidden'
  nextTick(() => {
    closeButtonRef.value?.focus()
  })
}

function closeModal() {
  activeModal.value = null
  document.body.style.overflow = ''
  ;(lastFocused.value as HTMLElement)?.focus()
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape' && activeModal.value !== null) closeModal()
}

onMounted(() => {
  document.addEventListener('keydown', onKeydown)
})

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
  if (activeModal.value !== null) document.body.style.overflow = ''
})
</script>

<style scoped>
.modal-overlay-enter-active {
  transition: opacity 0.2s ease;
}
.modal-overlay-leave-active {
  transition: opacity 0.2s ease;
}
.modal-overlay-enter-from,
.modal-overlay-leave-to {
  opacity: 0;
}
.modal-overlay-enter-active .modal-inner {
  animation: lf-modal-pop 0.28s ease forwards;
}
@keyframes lf-modal-pop {
  from { transform: scale(0.94); opacity: 0; }
  to   { transform: scale(1);    opacity: 1; }
}
@media (prefers-reduced-motion: reduce) {
  .modal-overlay-enter-active,
  .modal-overlay-leave-active { transition: none; }
  .modal-overlay-enter-active .modal-inner { animation: none; }
}
</style>
